// src/pages/Dashboard/Roles.jsx
import React, { useState } from 'react';
import { Plus } from 'lucide-react'; // ✅ Added Plus icon import
import { usePermissions } from '../../hooks/usePermissions';
import PermissionGuard from '../../components/RBAC/PermissionGuard';

const Roles = () => {
  const { hasPermission } = usePermissions();
  const [roles, setRoles] = useState([
    {
      id: 1,
      role_name: 'Super Admin',
      permissions: ['dashboard', 'orders.view', 'orders.edit', 'users.view', 'users.edit'],
      status: 1,
      hide: 0,
    },
    {
      id: 2,
      role_name: 'Admin',
      permissions: ['dashboard', 'orders.view', 'users.view'],
      status: 1,
      hide: 0,
    },
    {
      id: 3,
      role_name: 'Viewer',
      permissions: ['dashboard', 'orders.view'],
      status: 1,
      hide: 0,
    },
  ]);
  
  const allPermissions = [
    'dashboard',
    'orders.view',
    'orders.edit',
    'orders.create',
    'orders.delete',
    'users.view',
    'users.edit',
    'users.create',
    'users.delete',
    'restaurants.overview',
    'restaurants.edit',
    'restaurants.create',
    'restaurants.delete',
    'reports.view',
    'settings.view',
    'settings.edit',
    'roles.view',
    'roles.edit',
    'roles.create',
    'roles.delete',
  ];
  
  const togglePermission = (roleId, permission) => {
    setRoles(prev => prev.map(role => {
      if (role.id === roleId) {
        const hasPerm = role.permissions.includes(permission);
        return {
          ...role,
          permissions: hasPerm
            ? role.permissions.filter(p => p !== permission)
            : [...role.permissions, permission]
        };
      }
      return role;
    }));
  };
  
  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
          Role Management
        </h2>
        
        <PermissionGuard permission="roles.create">
          <button style={{
            padding: '10px 20px',
            background: '#6c63ff',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 500,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.opacity = '0.9'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            <Plus size={18} /> New Role
          </button>
        </PermissionGuard>
      </div>
      
      <div style={{
        display: 'grid',
        gap: '16px',
      }}>
        {roles.map((role) => (
          <div key={role.id} style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #e2e8f5',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
          onMouseLeave={(e) => e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              <div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: 700, 
                  color: '#0f172a', 
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}>
                  {role.role_name}
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '2px 10px',
                    borderRadius: '20px',
                    background: role.status === 1 ? '#22c55e' : '#ef4444',
                    color: '#fff',
                  }}>
                    {role.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                  {role.hide === 1 && (
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '2px 10px',
                      borderRadius: '20px',
                      background: '#6b7280',
                      color: '#fff',
                    }}>
                      Hidden
                    </span>
                  )}
                </h3>
                <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>
                  {role.permissions.length} permissions • Role ID: #{role.id}
                </div>
              </div>
              
              <PermissionGuard permission="roles.edit">
                <button style={{
                  padding: '6px 16px',
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                >
                  Edit Role
                </button>
              </PermissionGuard>
            </div>
            
            {/* Permission Categories */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '4px',
              marginTop: '8px',
            }}>
              {allPermissions.map((permission) => {
                const isChecked = role.permissions.includes(permission);
                const [module, action] = permission.split('.');
                
                return (
                  <label 
                    key={permission} 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: isChecked ? '#0f172a' : '#94a3b8',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      cursor: hasPermission('roles.edit') ? 'pointer' : 'default',
                      background: isChecked ? '#f0f4ff' : 'transparent',
                      transition: 'all 0.2s',
                      border: isChecked ? '1px solid rgba(108,99,255,0.15)' : '1px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (hasPermission('roles.edit')) {
                        e.target.style.background = isChecked ? '#e8edff' : '#f8fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (hasPermission('roles.edit')) {
                        e.target.style.background = isChecked ? '#f0f4ff' : 'transparent';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => togglePermission(role.id, permission)}
                      disabled={!hasPermission('roles.edit')}
                      style={{
                        accentColor: '#6c63ff',
                        cursor: hasPermission('roles.edit') ? 'pointer' : 'default',
                        width: '14px',
                        height: '14px',
                      }}
                    />
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}>
                      <span style={{
                        fontWeight: isChecked ? 600 : 400,
                        color: isChecked ? '#6c63ff' : '#94a3b8',
                      }}>
                        {module}
                      </span>
                      <span style={{
                        fontSize: '10px',
                        color: '#94a3b8',
                        background: '#f1f5f9',
                        padding: '1px 6px',
                        borderRadius: '10px',
                      }}>
                        {action}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            
            {/* Stats Footer */}
            <div style={{
              marginTop: '16px',
              paddingTop: '12px',
              borderTop: '1px solid #e2e8f5',
              display: 'flex',
              gap: '20px',
              fontSize: '12px',
              color: '#94a3b8',
            }}>
              <span>
                ✅ <strong>{role.permissions.length}</strong> permissions granted
              </span>
              <span>
                🔒 <strong>{allPermissions.length - role.permissions.length}</strong> permissions denied
              </span>
              <span>
                📅 Updated: {new Date().toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Roles;